import React, { useCallback, useState } from 'react';
import hash from 'object-hash';
import { FiClipboard, FiPrinter } from 'react-icons/fi';

import { useHistory } from 'react-router-dom';
import fhirpath from 'fhirpath';
import orderBy from 'lodash.orderby';
import partition from 'lodash.partition';

import { ReactComponent as Medicine } from '@/assets/icons/medicine.svg';
import { ReactComponent as Stethoscope } from '@/assets/icons/stethoscope.svg';
import { ReactComponent as Referral } from '@/assets/icons/referral.svg';
import Button from '@/components/Button';
import Markdown from '@/components/Markdown';
import SectionCard from '@/components/SectionCard';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';

import LoadingSwitchTransition from '@/components/LoadingSwitchTransition';
import PlaceholderLoading from '@/components/PlaceholderLoading';
import { protocoloEnterpriseRoute } from '@/features/enterprise/helpers';
import { SectionResumoType } from '@/features/enterprise/models/types';
import { medicationRequestTitle } from '@/features/fhir/helpers/medicationRequest';
import {
  ArtefatoCard,
  ArtefatoCardBody,
  ArtefatoCardTitle,
  Container,
  Content,
  FlexColumn,
  FlexRow,
  Header,
  ListaArtefatos,
  TipoSolicitacaoLabelTitle,
} from './styles';
import ModalPrintSumario from '../ModalPrintSumario';
import { useSolicitacoes } from '../../providers/SolicitacoesProvider';

const TipoSolicitacaoLabel = ({ tipo }: { tipo: string }) => {
  const dictionary = new Map();
  dictionary.set(
    'encaminhamento',
    <>
      <Referral />
      <TipoSolicitacaoLabelTitle>Encaminhamentos</TipoSolicitacaoLabelTitle>
    </>,
  );
  dictionary.set(
    'orientacoes-medicas',
    <>
      <FiClipboard size={20} />
      <TipoSolicitacaoLabelTitle>Orientações</TipoSolicitacaoLabelTitle>
    </>,
  );
  dictionary.set(
    'solicitacao-exame',
    <>
      <Stethoscope />
      <TipoSolicitacaoLabelTitle>Exames Prescritos</TipoSolicitacaoLabelTitle>
    </>,
  );
  dictionary.set(
    'prescricao-medica',
    <>
      <Medicine />
      <TipoSolicitacaoLabelTitle>
        Medicamentos Prescritos
      </TipoSolicitacaoLabelTitle>
    </>,
  );

  return dictionary.get(tipo);
};

const Artefatos = ({ items }: { items: fhir4.FhirResource[] }) => {
  const getTitulo = useCallback((artefato: fhir4.FhirResource) => {
    if (artefato.resourceType === 'MedicationRequest') {
      return medicationRequestTitle(artefato as fhir4.MedicationRequest);
    }

    if (artefato.resourceType === 'ServiceRequest') {
      return fhirpath.evaluate(
        artefato,
        'ServiceRequest.code.text | ServiceRequest.code.coding.display',
      )[0];
    }

    if (artefato.resourceType === 'CarePlan') {
      return fhirpath.evaluate(artefato, 'CarePlan.title')[0];
    }

    return '';
  }, []);

  const getDescricao = useCallback((artefato: fhir4.FhirResource) => {
    if (artefato.resourceType === 'MedicationRequest') {
      return fhirpath.evaluate(
        artefato as fhir4.MedicationRequest,
        'MedicationRequest.dosageInstruction.text',
      )[0];
    }

    if (artefato.resourceType === 'ServiceRequest') {
      const serviceRequestNote = fhirpath.evaluate(
        artefato,
        'ServiceRequest.reasonCode.text',
      )[0];
      return serviceRequestNote || '';
    }

    return '';
  }, []);

  return (
    <ListaArtefatos>
      {items.map(item => {
        const titulo = getTitulo(item);
        const descricao = getDescricao(item);
        return (
          <ArtefatoCard key={item.id || hash(item)}>
            <ArtefatoCardTitle>{titulo}</ArtefatoCardTitle>
            {!!descricao && <ArtefatoCardBody>{descricao}</ArtefatoCardBody>}
          </ArtefatoCard>
        );
      })}
    </ListaArtefatos>
  );
};

const SectionItem = ({
  section,
}: // artefatos = [],
{
  section: SectionResumoType;
  // artefatos: fhir4.FhirResource[][];
}) => {
  const { solicitacoes } = useSolicitacoes();

  if (section.modelo === 'encaminhamento') {
    const encaminhamentos = solicitacoes
      .filter(item => item.tipo === 'encaminhamento')
      .map(item => item.pedido);
    return (
      <SectionCard
        key={section.codigo}
        itemsCounter={encaminhamentos.length}
        collapsed={section.collapse}
        title={<TipoSolicitacaoLabel tipo="encaminhamento" />}
      >
        <Artefatos items={encaminhamentos} />
      </SectionCard>
    );
  }

  if (section.modelo === 'orientacoes-medicas') {
    const orientacoes = solicitacoes
      .filter(item => item.tipo === 'orientacoes-medicas')
      .map(item => item.pedido);
    return (
      <SectionCard
        key={section.codigo}
        itemsCounter={orientacoes.length}
        collapsed={section.collapse}
        title={<TipoSolicitacaoLabel tipo="orientacoes-medicas" />}
      >
        <Artefatos items={orientacoes} />
      </SectionCard>
    );
  }

  if (section.modelo === 'solicitacoes-exame') {
    const exames = solicitacoes
      .filter(item => item.tipo === 'solicitacao-exame')
      .map(item => item.pedido);

    return (
      <SectionCard
        key={section.codigo}
        itemsCounter={exames.length}
        collapsed={section.collapse}
        title={<TipoSolicitacaoLabel tipo="solicitacao-exame" />}
      >
        <Artefatos items={exames} />
      </SectionCard>
    );
  }

  if (section.modelo === 'solicitacoes-medicamento') {
    const medicamentos = solicitacoes
      .filter(item => item.tipo === 'prescricao-medica')
      .map(item => item.pedido);

    return (
      <SectionCard
        key={section.codigo}
        itemsCounter={medicamentos.length}
        collapsed={section.collapse}
        title={<TipoSolicitacaoLabel tipo="prescricao-medica" />}
      >
        <Artefatos items={medicamentos} />
      </SectionCard>
    );
  }

  if (section.texto_renderizado) {
    return (
      <SectionCard
        key={section.codigo}
        collapsed={section.collapse}
        title={section.titulo}
      >
        <Markdown text={section.texto_renderizado || ''} />
      </SectionCard>
    );
  }

  return <></>;
};

const PlaceholderContent = () => (
  <PlaceholderLoading.Rect
    height={513}
    width="100%"
    style={{
      display: 'flex',
      borderRadius: 16,
    }}
  />
);

const ProtocoloExecutadoSumario: React.FC<{ showBackToProtocol?: boolean }> = ({
  showBackToProtocol = false,
}) => {
  const [{ protocoloExecutado }] = useExecucaoContext();
  // const [{ solicitacoes, bundle }] = useEnterpriseContext();
  const { solicitacoes } = useSolicitacoes();

  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const redirectToInstance = React.useCallback(() => {
    if (protocoloExecutado) {
      history.replace(protocoloEnterpriseRoute(protocoloExecutado));
    }
  }, [history, protocoloExecutado]);

  const encaminhamentos = React.useMemo(() => {
    return solicitacoes
      .filter(item => item.tipo === 'encaminhamento')
      .map(item => item.pedido);
  }, [solicitacoes]);

  const orientacoes = React.useMemo(() => {
    return solicitacoes
      .filter(item => item.tipo === 'orientacoes-medicas')
      .map(item => item.pedido);
  }, [solicitacoes]);

  const exames = React.useMemo(() => {
    return solicitacoes
      .filter(item => item.tipo === 'solicitacao-exame')
      .map(item => item.pedido);
  }, [solicitacoes]);

  const medicamentos = React.useMemo(() => {
    return solicitacoes
      .filter(item => item.tipo === 'prescricao-medica')
      .map(item => item.pedido);
  }, [solicitacoes]);

  const sortedSections = React.useMemo<SectionResumoType[][]>(() => {
    // if (!protocoloExecutado?.sumario) return [[], [], []];
    const sorted = orderBy(protocoloExecutado?.sumario, ['ordem']).filter(
      item => {
        if (item.modelo === 'encaminhamento') {
          return !!encaminhamentos.length;
        }
        if (item.modelo === 'orientacoes-medicas') {
          return !!orientacoes.length;
        }

        if (item.modelo === 'solicitacoes-exame') {
          return !!exames.length;
        }
        if (item.modelo === 'solicitacoes-medicamento') {
          return !!medicamentos.length;
        }

        // Apenas itens renderizáveis devem ocupar espaço
        return item.status === 'completed';
      },
    );
    const [withDisplayBlock, _] = partition(sorted, item => item.display_block);

    const splitIndex = Math.ceil(_.length / 2);
    const firstHalf = _.slice(0, splitIndex);
    const secondHalf = _.slice(splitIndex, _.length);

    return [withDisplayBlock, firstHalf, secondHalf];
  }, [
    exames.length,
    medicamentos.length,
    orientacoes.length,
    encaminhamentos.length,
    protocoloExecutado?.sumario,
  ]);

  React.useEffect(() => {
    if (protocoloExecutado) {
      setIsLoading(false);
    }
  }, [protocoloExecutado]);

  return (
    <LoadingSwitchTransition
      loading={isLoading}
      LoadingComponent={<PlaceholderContent />}
      style={{
        flex: 1,
        width: '100%',
      }}
    >
      <ModalPrintSumario
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
      />
      <Container>
        <Header>
          <h2>Resumo do Atendimento</h2>

          <div>
            {showBackToProtocol && (
              <Button
                theme="secondary"
                onClick={() => {
                  redirectToInstance();
                }}
              >
                Voltar para o protocolo
              </Button>
            )}
            <Button
              theme="secondary"
              onClick={() => {
                setIsPrintModalOpen(true);
              }}
            >
              <FiPrinter size={16} />
              Imprimir
            </Button>
          </div>
        </Header>
        <Content>
          <FlexRow>
            {sortedSections[0].map(section => (
              <SectionItem key={section.codigo} section={section} />
            ))}
          </FlexRow>
          <FlexRow>
            {/* Coluna 1 */}
            <FlexColumn>
              {sortedSections[1].map(section => (
                <SectionItem key={section.codigo} section={section} />
              ))}
            </FlexColumn>

            {/* Coluna 2 */}
            <FlexColumn>
              {sortedSections[2].map(section => (
                <SectionItem key={section.codigo} section={section} />
              ))}
            </FlexColumn>
          </FlexRow>
        </Content>
      </Container>
    </LoadingSwitchTransition>
  );
};

export default ProtocoloExecutadoSumario;
